<template>
  <div class="katsina-lga-map">
    <div class="row mb-2">
      <div class="col-12">
        <span class="text-sm text-muted">
          <i class="fa fa-user-friends me-1"></i>
          Pensioner count by Local Government of origin, or residence LGA where origin is not recorded &mdash; darker shades indicate more pensioners
        </span>
      </div>
    </div>
    <div v-if="!hasData" style="height: 620px;">
      <vsud-nodata></vsud-nodata>
    </div>
    <div v-show="hasData" class="map-wrap">
      <div class="map-zoom-controls">
        <button type="button" class="map-zoom-btn" title="Zoom in" @click="zoomBy(1.3)">
          <i class="fa fa-plus"></i>
        </button>
        <button type="button" class="map-zoom-btn" title="Zoom out" @click="zoomBy(0.7692)">
          <i class="fa fa-minus"></i>
        </button>
        <button type="button" class="map-zoom-btn" title="Reset" @click="resetZoom">
          <i class="fa fa-expand"></i>
        </button>
      </div>
      <div ref="chart" style="width: 100%; height: 620px;"></div>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";
import katsinaGeoJson from "@/assets/geo/katsina-lga.geo.json";
import VsudNodata from "@/components/VsudNodata.vue";

const MAP_NAME = "katsina";

export default {
  name: "katsina-lga-map",

  components: { VsudNodata },

  props: {
    dataSet: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      metric: "total",
      chart: null,
      canonicalByKey: {},
    };
  },

  computed: {
    hasData() {
      return Array.isArray(this.dataSet) && this.dataSet.length > 0;
    },

    metricConfig() {
      // Single-hue green ramp, shaded by pensioner count (Geep-style density map).
      return {
        label: "Pensioners",
        colors: ["#e5f5e0", "#a1d99b", "#41ab5d", "#006d2c", "#00441b"],
        currency: false,
      };
    },
  },

  watch: {
    dataSet: {
      deep: true,
      handler() {
        this.renderChart();
      },
    },
    metric() {
      this.renderChart();
    },
  },

  mounted() {
    this.buildCanonicalLookup();
    if (!echarts.getMap(MAP_NAME)) {
      echarts.registerMap(MAP_NAME, katsinaGeoJson);
    }
    this.chart = echarts.init(this.$refs.chart);
    this.renderChart();
    window.addEventListener("resize", this.handleResize);
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },

  methods: {
    handleResize() {
      if (this.chart) this.chart.resize();
    },

    zoomBy(factor) {
      if (!this.chart) return;
      const opt = this.chart.getOption();
      const series = opt.series && opt.series[0];
      if (!series) return;
      const current = series.zoom || 1;
      const next = Math.max(0.8, Math.min(8, current * factor));
      this.chart.setOption({ series: [{ zoom: next, center: series.center }] });
    },

    resetZoom() {
      if (!this.chart) return;
      this.chart.setOption({ series: [{ zoom: 1.15, center: null }] });
    },

    normalizeKey(name) {
      return String(name || "").toLowerCase().replace(/[^a-z]/g, "");
    },

    buildCanonicalLookup() {
      const lookup = {};
      katsinaGeoJson.features.forEach((f) => {
        const name = f.properties.name;
        lookup[this.normalizeKey(name)] = name;
      });
      // Known DB spelling variants -> canonical GeoJSON name
      const aliases = {
        zangon: "Zango",
        danmusa: "Dan Musa",
        dutsinma: "Dutsin-Ma",
        maiadua: "Mai'Adua",
        mashi: "Mashi",
      };
      Object.keys(aliases).forEach((k) => {
        if (lookup[this.normalizeKey(aliases[k])]) lookup[k] = aliases[k];
      });
      this.canonicalByKey = lookup;
    },

    metricValue(row) {
      const num = (v) => (v === null || v === undefined || v === "" ? 0 : parseInt(v, 10) || 0);
      switch (this.metric) {
        case "verified":
          return num(row.verified);
        case "not_verified":
          // Not-verified = pending + failed when present, else total - verified
          if (row.pending !== undefined || row.failed !== undefined) {
            return num(row.pending) + num(row.failed);
          }
          return Math.max(num(row.total) - num(row.verified), 0);
        case "amount":
          return num(row.pension_amount);
        case "total":
        default:
          return num(row.total);
      }
    },

    buildData() {
      const byName = {};
      const unmatched = [];

      (this.dataSet || []).forEach((row) => {
        const rawName = row.lga || row.location_lga;
        const key = this.normalizeKey(rawName);
        const canonical = this.canonicalByKey[key];
        if (!canonical) {
          if (rawName) unmatched.push(rawName);
          return;
        }
        // Merge duplicates (should not happen, but be safe)
        if (!byName[canonical]) {
          byName[canonical] = { ...row, location_lga: canonical };
        } else {
          ["total", "verified", "pending", "failed", "pension_amount", "origin_count", "residence_count"].forEach((f) => {
            if (row[f] === undefined) return;
            byName[canonical][f] = (parseInt(byName[canonical][f], 10) || 0) + (parseInt(row[f], 10) || 0);
          });
        }
      });

      if (unmatched.length) {
        // eslint-disable-next-line no-console
        console.warn("[KatsinaLgaMap] Unmatched location_lga values (not drawn):", [...new Set(unmatched)]);
      }

      return katsinaGeoJson.features.map((f) => {
        const name = f.properties.name;
        const row = byName[name];
        return {
          name,
          value: row ? this.metricValue(row) : 0,
          raw: row || null,
        };
      });
    },

    formatNumber(v) {
      return (parseInt(v, 10) || 0).toLocaleString("en-us");
    },

    renderChart() {
      if (!this.chart) return;

      const data = this.buildData();
      const values = data.map((d) => d.value);
      const max = Math.max(1, ...values);
      const cfg = this.metricConfig;
      const currency = cfg.currency;
      const self = this;

      this.chart.setOption(
        {
          tooltip: {
            trigger: "item",
            formatter(params) {
              const raw = params.data && params.data.raw;
              const money = (v) => "NGN " + self.formatNumber(v);
              let rows =
                '<div style="font-weight:600;margin-bottom:4px;">' + params.name + "</div>";
              if (!raw) {
                rows += '<div style="color:#888;">No data</div>';
                return rows;
              }
              const line = (label, val) =>
                '<div style="display:flex;justify-content:space-between;gap:14px;">' +
                '<span style="color:#666;">' + label + "</span><span style=\"font-weight:600;\">" + val + "</span></div>";
              rows += line("Total", self.formatNumber(raw.total));
              if (raw.origin_count !== undefined && raw.residence_count !== undefined) {
                rows += line("&nbsp;&nbsp;By origin", self.formatNumber(raw.origin_count));
                rows += line("&nbsp;&nbsp;By residence", self.formatNumber(raw.residence_count));
              }
              rows += line("Verified", self.formatNumber(raw.verified));
              const notVerified =
                raw.pending !== undefined || raw.failed !== undefined
                  ? (parseInt(raw.pending, 10) || 0) + (parseInt(raw.failed, 10) || 0)
                  : Math.max((parseInt(raw.total, 10) || 0) - (parseInt(raw.verified, 10) || 0), 0);
              rows += line("Not-Verified", self.formatNumber(notVerified));
              rows += line("Pension amount", money(raw.pension_amount));
              return rows;
            },
          },
          visualMap: {
            type: "continuous",
            min: 0,
            max,
            left: "left",
            bottom: 10,
            calculable: true,
            text: ["High", "Low"],
            seriesIndex: 0,
            inRange: { color: cfg.colors },
            formatter(value) {
              return currency ? "NGN " + self.formatNumber(value) : self.formatNumber(value);
            },
          },
          series: [
            {
              name: cfg.label,
              type: "map",
              map: MAP_NAME,
              roam: true,
              zoom: 1.15,
              layoutCenter: ["50%", "50%"],
              layoutSize: "100%",
              scaleLimit: { min: 0.8, max: 8 },
              label: {
                show: true,
                fontSize: 9,
                color: "#333",
              },
              emphasis: {
                label: { show: true, fontSize: 10, fontWeight: "bold" },
                itemStyle: { areaColor: "#f5b301" },
              },
              itemStyle: {
                borderColor: "#ffffff",
                borderWidth: 0.6,
                areaColor: "#eef1f5",
              },
              data,
            },
          ],
        },
        true
      );
    },
  },
};
</script>

<style scoped>
.katsina-lga-map .form-control {
  height: calc(2.25rem + 2px);
}

.map-wrap {
  position: relative;
}

.map-zoom-controls {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.map-zoom-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #ffffff;
  color: #344767;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  transition: background 0.15s ease, color 0.15s ease;
}

.map-zoom-btn:hover {
  background: #f5b301;
  color: #ffffff;
  border-color: #f5b301;
}
</style>
